import { BoolFilter } from "gen/search/service/service_pb"
import { Vendor } from "gen/vendors/models_pb"
import { PlugIcon } from "lucide-react"
import { useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import {
  usePopoverSelect,
  UsePopoverSelectProps,
  UsePopoverSelectRes,
} from "shared-web/components/composite/popover"

import { useSearchVendors } from "features/api/search"

interface UseVendorsPopoverSelectProps
  extends Pick<
    UsePopoverSelectProps<Vendor>,
    "selected" | "onSelect" | "defaultSelected"
  > {
  filter?: (vendor: Vendor) => boolean
}

type UseVendorsPopoverSelectRes = UsePopoverSelectRes<Vendor>

function useVendorsPopoverSelect(
  props: UseVendorsPopoverSelectProps
): UseVendorsPopoverSelectRes {
  const { results, setQuery } = useSearchVendors(
    { isActiveFilter: BoolFilter.TRUE },
    { ignoreSearchQuery: true }
  )

  const { filter } = props
  const vendors = useMemo(() => {
    const res: Vendor[] = []
    for (const r of results) {
      if (!r.vendor) continue
      if (filter && !filter(r.vendor)) continue
      res.push(r.vendor)
    }
    return res
  }, [results, filter])

  return usePopoverSelect({
    items: vendors,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    onChangeQuery: setQuery,
    ...props,
  })
}

function keyExtractor(vendor: Vendor): string {
  return UUID.idString(vendor.id)
}

function labelExtractor(vendor: Vendor): string {
  return vendor.name
}

function accessoryExtractor(vendor: Vendor): React.ReactNode | null {
  if (!vendor.integrationVendor) {
    return null
  }
  return <PlugIcon className="text-icon-subtle h-4 w-4" />
}

export default useVendorsPopoverSelect
